import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { validateRedirectUrl, logValidationResult, getSupabaseConfigInstructions } from '../utils/redirectUrlValidator';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // ✅ LOAD PROFILE + TEAM INFO - Fixed to handle missing profiles and null team_id
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        // First, try to get the basic profile
        const { data: profileData, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.maybeSingle(); // Changed from .single() to .maybeSingle()

        if (profileError) {
          console.error('Profile load error:', profileError);
          setUserProfile(null);
          return;
        }

        // If no profile exists, clear the profile state
        if (!profileData) {
          console.warn('No profile found for user:', userId);
          setUserProfile(null);
          return;
        }

        // If profile has team_id, fetch team details separately
        if (profileData?.team_id) {
          const { data: teamData, error: teamError } = await supabase
            ?.from('teams')
            ?.select('id, team_name, team_number')
            ?.eq('id', profileData?.team_id)
            ?.maybeSingle();

          if (teamError) {
            console.error('Team load error:', teamError);
          }

          // Combine profile with team data
          setUserProfile({
            ...profileData,
            team: teamData || null
          });
        } else {
          // No team assigned, just set the profile
          setUserProfile({
            ...profileData,
            team: null
          });
        }
      } catch (error) {
        console.error('Profile load error:', error);
        setUserProfile(null);
      } finally {
        setProfileLoading(false);
      }
    },
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    },
  };

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) profileOperations?.load(session?.user?.id);
      else profileOperations?.clear();
    },
  };

  useEffect(() => {
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(authStateHandlers?.onChange);
    supabase?.auth?.getSession()?.then(({ data }) => authStateHandlers?.onChange(null, data?.session));
    return () => subscription?.unsubscribe();
  }, []);

  // ✅ SIGN IN
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });

      if (error) {
        let message = 'Login failed. Please try again.';
        if (error?.status === 429) message = 'Too many attempts. Try again soon.';
        else if (error?.message?.includes('Invalid login credentials')) message = 'Invalid email or password.';
        else if (error?.message?.includes('Email not confirmed')) message = 'Please confirm your email first.';
        return { data: null, error: { message, code: error?.code || 'LOGIN_FAILED' } };
      }

      const user = data?.user;
      if (user && !user?.email_confirmed_at) {
        await supabase?.auth?.signOut();
        return { data: null, error: { message: 'Please confirm your email before logging in.', code: 'EMAIL_NOT_CONFIRMED' } };
      }

      // Automatically load their profile + team info
      if (user) await profileOperations?.load(user?.id);

      return { data, error: null };
    } catch (err) {
      console.error('Unexpected login error:', err);
      return { data: null, error: { message: 'Unexpected error. Please try again.', code: 'NETWORK_ERROR' } };
    }
  };

  // ✅ SIGN UP - Fixed to use secure RPC function
  const signUp = async (email, password, userData) => {
    try {
      console.log('🟡 Starting signup for:', email);
  
      // Step 1️⃣: Check if a profile already exists
      const normalizedEmail = email?.toLowerCase()?.trim();
      const { data: existingUser, error: existingError } = await supabase?.from('user_profiles')?.select('email')?.eq('email', normalizedEmail)?.maybeSingle();
  
      console.log('🔹 Existing user check:', { existingUser, existingError });
  
      if (existingUser) {
        console.warn('⚠️ Email already exists in user_profiles');
        return {
          data: null,
          error: { code: 'EMAIL_EXISTS', message: 'An account already exists with this email. Please log in instead.' },
        };
      }
  
      // Step 2️⃣: Create Auth User
      const emailRedirectUrl = `${window.location?.origin}/user-login`;
      
      // Validate redirect URL before making the API call
      const validation = validateRedirectUrl(emailRedirectUrl, 'email confirmation');
      logValidationResult(validation, emailRedirectUrl, 'email confirmation');
      
      // If validation fails, return error immediately
      if (!validation?.isValid) {
        console.error('❌ Email redirect URL validation failed:', validation?.error);
        return { 
          data: null, 
          error: { 
            message: `Invalid email confirmation redirect URL: ${validation?.error}. Please contact your administrator.`,
            code: 'INVALID_REDIRECT_URL' 
          } 
        };
      }
      
      // Log warnings if any
      if (validation?.warnings?.length > 0) {
        console.warn('⚠️ Email redirect URL validation warnings:');
        validation?.warnings?.forEach(warning => console.warn(warning));
      }
      
      const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: { full_name: userData?.fullName },
          emailRedirectTo: emailRedirectUrl
        },
      });
  
      console.log('🔹 Auth signup result:', { signUpData, signUpError });
  
      if (signUpError) {
        // Check for redirect URL not whitelisted error
        if (signUpError?.message?.toLowerCase()?.includes('redirect') || 
            signUpError?.message?.toLowerCase()?.includes('url') ||
            signUpError?.message?.toLowerCase()?.includes('not allowed')) {
          const instructions = getSupabaseConfigInstructions(emailRedirectUrl);
          console.error('❌ REDIRECT URL NOT WHITELISTED FOR EMAIL CONFIRMATION');
          console.error('Required Action: Add the following URL to Supabase allowed redirect URLs:');
          console.error(`  → ${emailRedirectUrl}`);
          console.error('Steps:');
          instructions?.steps?.forEach(step => console.error(step));
          return { 
            data: null, 
            error: { 
              message: `Configuration Error: Email confirmation redirect URL (${emailRedirectUrl}) is not whitelisted. Please contact your administrator.`,
              code: 'REDIRECT_URL_NOT_ALLOWED' 
            } 
          };
        }
        return { data: null, error: { message: 'Signup failed. Please try again.', code: 'SIGNUP_FAILED' } };
      }
  
      const user = signUpData?.user;
      if (!user) {
        console.error('⚠️ No user returned after signup');
        return { data: null, error: { message: 'No user data returned.', code: 'NO_USER' } };
      }
  
      // Step 3️⃣: Get role/team info for this email
      const { data: roleAuth, error: roleAuthError } = await supabase?.from('role_email_authorizations')?.select('role, team_role, team_number')?.eq('email', normalizedEmail)?.maybeSingle();
  
      console.log('🔹 Role auth lookup:', { roleAuth, roleAuthError });
  
      if (roleAuthError) console.error('⚠️ Role lookup failed:', roleAuthError);
  
      // Step 4️⃣: Find the team_id from the team_number
      let teamId = null;
      if (roleAuth?.team_number) {
        const { data: teamData, error: teamError } = await supabase?.from('teams')?.select('id, team_number')?.eq('team_number', roleAuth?.team_number)?.maybeSingle();
  
        console.log('🔹 Team lookup result:', { teamData, teamError });
  
        if (teamError) console.error('⚠️ Team lookup failed:', teamError);
        teamId = teamData?.id || null;
      } else {
        console.warn('⚠️ No team_number found in role_email_authorizations');
      }
  
      // 🔧 ENHANCED FIX: Properly format arrays for PostgreSQL text[] type
      const formatTextArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr?.length === 0) {
          return null;
        }
        return arr?.filter(item => item && typeof item === 'string' && item?.trim() !== '');
      };

      const interestsValue = formatTextArray(userData?.interests);
      const skillsValue = formatTextArray(userData?.skills);
  
      // Step 5️⃣: Use the secure RPC function to create profile (bypasses RLS)
      // 🚨 CRITICAL FIX: Pass user_id as separate parameter, not inside profile_data
      const profileData = {
        email: user?.email,
        full_name: userData?.fullName,
        role: roleAuth?.role || userData?.role || 'user',
        team_role: roleAuth?.team_role || null,
        team_id: teamId,
        organization: userData?.organization || null,
        title: userData?.title || null,
        location: userData?.location || null,
        bio: userData?.bio || null,
        phone: userData?.phone || null,
        linkedin_url: userData?.linkedinUrl || null,
        website_url: userData?.websiteUrl || null,
        interests: interestsValue,
        skills: skillsValue,
        city: userData?.city || null,
        department: userData?.department || null,
        university: userData?.university || null,
        major: userData?.major || null,
        academic_year: userData?.academicYear || null,
        expected_graduation: userData?.expectedGraduation || null
      };
  
      console.log('🟢 Calling secure create_user_profile function with user_id:', user?.id);
      console.log('🟢 Profile data:', JSON.stringify(profileData, null, 2));
  
      // 🔧 FIXED: Pass user_id as separate parameter matching migration 20250226000006
      const { data: profileResult, error: profileError } = await supabase?.rpc('create_user_profile', {
          user_id: user?.id,
          profile_data: profileData
        });
  
      console.log('🔹 Profile creation result:', { profileResult, profileError });
  
      if (profileError) {
        console.error('❌ Failed to create profile via RPC:', profileError);
        return { 
          data: signUpData, 
          error: { 
            message: 'Failed to create user profile: ' + (profileError?.message || 'Unknown error'),
            code: 'PROFILE_CREATION_FAILED' 
          }
        };
      }
  
      // Check if the function returned success
      if (profileResult && !profileResult?.success) {
        console.error('❌ Profile creation function returned error:', profileResult?.error);
        return { 
          data: signUpData, 
          error: { 
            message: profileResult?.error || 'Failed to create profile',
            code: 'PROFILE_CREATION_FAILED' 
          }
        };
      }
  
      console.log('✅ Profile created successfully!');
      
      // Load the newly created profile
      if (profileResult?.data) {
        setUserProfile(profileResult?.data);
      }
  
      return { data: signUpData, error: null };
    } catch (err) {
      console.error('🔥 Unexpected signup error:', err);
      return {
        data: null,
        error: { message: err?.message || 'Unexpected registration error. Please try again.', code: 'NETWORK_ERROR' },
      };
    }
  };
  
  
  
  

  // ✅ SIGN OUT
  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        profileOperations?.clear();
      }
      return { error };
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  // ✅ UPDATE PROFILE
  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      if (!error) setUserProfile(data);
      return { data, error };
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile, // includes team info now
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};